import { gql } from '@apollo/client'
import { BodyText, Card, Heading } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import classNames from 'classnames'
import { HTMLAttributes } from 'react'

import { JusAILogo } from '../ai-offer-lock/jus-ai-logo'
import { BenefitsList } from '../benefits-list'
import { CheckoutButton } from '../checkout-button'
import { CheckoutButton_OfferLockCardFragment } from '../checkout-button/__generated__/checkout-button.graphql'
import { OneClickButton } from '../one-click-button'
import {
  AiTwoOfferPlanCard_OfferLockCardFragment,
  AiTwoOfferPlanCard_ViewerFragment,
} from './__generated__/ai-two-offer-plan-card.graphql'
import styles from './ai-two-offer-plan-card.module.scss'

export interface AiTwoOfferPlanCardProps extends HTMLAttributes<HTMLDivElement> {
  viewer: AiTwoOfferPlanCard_ViewerFragment
  offerLockCard: AiTwoOfferPlanCard_OfferLockCardFragment
  className?: string
  showCreditCardAndRemoveOneClick?: boolean // this prop will be removed after move one click logic to checkout @ayrton
}

export function AiTwoOfferPlanCard(props: AiTwoOfferPlanCardProps) {
  const { offerLockCard, className, viewer, showCreditCardAndRemoveOneClick, ...rest } = props

  const cardClasses = classNames(styles['plan-two-offers-card'], className)

  const { title, offer } = offerLockCard
  const {
    priceTitle,
    priceText,
    frequency,
    frequencyPeriodText,
    priceAfterPromotionalPeriodText,
    installmentsText,
  } = offer
  const isJusAIOffer = title === 'Avançado +'
  const isPromotional = !!priceAfterPromotionalPeriodText
  return (
    <Card className={cardClasses} {...rest}>
      {isJusAIOffer ? (
        <Heading className={styles['ai-plan-two-offers-card-title']} size="lg" asChild>
          <div className={styles['ai-two-offer-lock-card-logo']}>
            <h4>{title}</h4>
            <JusAILogo width={80} height={22} />
          </div>
        </Heading>
      ) : (
        <Heading className={styles['ai-plan-two-offers-card-title']} size="lg" asChild>
          <h4>{title}</h4>
        </Heading>
      )}

      <Price size="md" className={styles['ai-two-offer-lock-card-price']}>
        {isPromotional ? (
          <Price.Discount>
            de <Price.DiscountStrike>{priceAfterPromotionalPeriodText}</Price.DiscountStrike> por:
          </Price.Discount>
        ) : installmentsText ? (
          <BodyText data-testid="price-label" size="md">
            {priceTitle}
            <strong className={styles['ai-two-offer-lock-card-price-installments']}>
              {' '}
              {installmentsText}
            </strong>{' '}
            de:
          </BodyText>
        ) : (
          <BodyText size="md">
            <span dangerouslySetInnerHTML={{ __html: priceTitle }} />
          </BodyText>
        )}
        <Price.Value>
          {priceText}
          {!isPromotional && !installmentsText && (
            <Price.ValueFrequency>{frequency}</Price.ValueFrequency>
          )}
        </Price.Value>
        {isPromotional && <Price.Helptext>no {frequencyPeriodText}</Price.Helptext>}
      </Price>

      <div className={styles['ai-two-offer-lock-card-buttons']}>
        {showCreditCardAndRemoveOneClick ? (
          <>
            <CheckoutButton offerLockCard={offerLockCard} kind="secondary" />
            <CheckoutButton
              kind="secondary"
              offerLockCard={{
                ...offerLockCard,
                creditCard: {
                  ...offerLockCard.creditCard,
                  ccToken: null,
                } as CheckoutButton_OfferLockCardFragment['creditCard'],
              }}
            />
          </>
        ) : (
          <>
            <CheckoutButton offerLockCard={offerLockCard} kind="secondary" />
            <OneClickButton offerLockCard={offerLockCard} viewer={viewer} kind="secondary" />
          </>
        )}
      </div>

      <BenefitsList
        offerLockCard={offerLockCard}
        benefitsListClassName={styles['ai-two-offer-lock-card-benefits']}
      />
    </Card>
  )
}

AiTwoOfferPlanCard.fragments = {
  offerLockCard: gql`
    fragment AiTwoOfferPlanCard_offerLockCard on OfferLockCard {
      title
      offer {
        priceTitle
        priceText
        frequency
        frequencyPeriodText
        priceAfterPromotionalPeriodText
        installmentsText
      }
      ...OneClickButton_offerLockCard
      ...CheckoutButton_offerLockCard
      ...BenefitsList_offerLockCard
    }
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${BenefitsList.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment AiTwoOfferPlanCard_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
